import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Plug, ExternalLink, Trash2, Check, Lock, Info } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-integration extra fields. Each entry is array of {name, label, placeholder, required, optional}
// Defaults to single api_key field when not specified.
const EXTRA_FIELDS = {
  twilio:         [{ name: "account_sid", label: "Account SID", placeholder: "ACxxxx…", required: true }],
  whatsapp_cloud: [{ name: "phone_number_id", label: "Phone Number ID", placeholder: "1234567890", required: true }],
  dialog360:      [{ name: "channel_id", label: "Channel ID", placeholder: "abc-123", required: false }],
  hubspot:        [{ name: "portal_id", label: "Portal ID (optional)", placeholder: "12345678", required: false }],
  pipedrive:      [{ name: "company_domain", label: "Company domain", placeholder: "yourco.pipedrive.com", required: true }],
  salesforce:     [{ name: "instance_url", label: "Instance URL", placeholder: "https://yourorg.my.salesforce.com", required: true }],
  zoho_crm:       [{ name: "datacenter", label: "Datacenter", placeholder: "us | eu | in | au", required: false }],
  stripe:         [{ name: "webhook_secret", label: "Webhook signing secret (optional)", placeholder: "whsec_…", required: false }],
  ga4:            [{ name: "measurement_id", label: "Measurement ID (G-XXXX)", placeholder: "G-XXXXXXXXXX", required: true }],
  meta_pixel:     [{ name: "pixel_id", label: "Pixel ID", placeholder: "1234567890", required: true }],
  gtm:            [{ name: "container_id", label: "Container ID (GTM-XXXX)", placeholder: "GTM-XXXXXX", required: true }],
};

export default function IntegrationConfigModal({ open, integration, onClose, onSaved }) {
  const { t } = useLang();
  const [apiKey, setApiKey] = useState("");
  const [extras, setExtras] = useState({});
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Reset + load existing config when opened
  useEffect(() => {
    if (!open || !integration) return;
    setApiKey("");
    setExtras({});
    setExisting(null);
    setLoadingExisting(true);
    axios
      .get(`${API}/integrations/${integration.key}/secrets`)
      .then(({ data }) => { if (data.configured) setExisting(data); })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  }, [open, integration]);

  if (!open || !integration) return null;

  const fields = EXTRA_FIELDS[integration.key] || [];
  const requiresKey = integration.needs_key !== false;

  const canSubmit = (() => {
    if (requiresKey && !apiKey.trim()) return false;
    for (const f of fields) {
      if (f.required && !(extras[f.name] || "").trim()) return false;
    }
    return true;
  })();

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/integrations/${integration.key}/secrets`, {
        api_key: apiKey,
        extras,
      });
      toast.success(t("cfg.saved", { name: data.name || integration.name }));
      onSaved?.(data);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("cfg.save_failed");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm(t("cfg.disconnect_confirm", { name: integration.name }))) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/integrations/${integration.key}/secrets`);
      toast.success(t("cfg.disconnected", { name: integration.name }));
      onSaved?.({ disconnected: true });
      onClose();
    } catch (_e) { toast.error(t("cfg.save_failed")); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid="integration-config-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] al-card p-0 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--al-border)]">
          <div className="flex items-center gap-2 min-w-0">
            <Plug className="w-4 h-4 text-[#3B82F6] shrink-0" />
            <h2 className="text-[15px] font-bold text-[var(--al-text)] truncate">
              {existing ? t("cfg.title_update", { name: integration.name }) : t("cfg.title_connect", { name: integration.name })}
            </h2>
          </div>
          <button type="button" data-testid="cfg-close" onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-3">
          {loadingExisting ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" />
            </div>
          ) : existing && (
            <div data-testid="cfg-existing" className="rounded-lg border border-[#16A34A]/40 bg-[#DCFCE7]/50 px-3 py-2 text-[12px] text-[#166534]">
              <div className="flex items-center gap-1.5 font-semibold">
                <Check className="w-3.5 h-3.5" />
                {t("cfg.already_connected")}
              </div>
              <div className="mt-1 opacity-80 font-mono">
                {t("cfg.current_key")}: {existing.masked_key || "—"}
              </div>
            </div>
          )}

          <p className="text-[12.5px] text-[var(--al-text-muted)] leading-relaxed">
            {integration.desc_long || integration.desc}
          </p>

          <div className="rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] p-3 text-[11.5px] text-[var(--al-text-muted)] flex items-start gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#3B82F6]" />
            <div>
              {t("cfg.where_to_get")}{" "}
              <a
                href={integration.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3B82F6] hover:underline inline-flex items-center gap-1"
              >
                {integration.url} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {requiresKey && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1 flex items-center gap-1">
                <Lock className="w-3 h-3" /> {t("cfg.field_api_key")}
              </label>
              <input
                type="password"
                data-testid="cfg-api-key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={existing ? t("cfg.placeholder_keep") : "sk-…"}
                className="al-input h-10 w-full text-[13px]"
                autoFocus
              />
              <p className="text-[10.5px] text-[var(--al-text-muted)] mt-1">
                {t("cfg.never_displayed")}
              </p>
            </div>
          )}

          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">
                {f.label}{f.required ? " *" : ""}
              </label>
              <input
                type="text"
                data-testid={`cfg-field-${f.name}`}
                value={extras[f.name] || ""}
                onChange={(e) => setExtras((prev) => ({ ...prev, [f.name]: e.target.value }))}
                placeholder={f.placeholder}
                className="al-input h-10 w-full text-[13px]"
              />
            </div>
          ))}
        </div>

        <footer className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[var(--al-border)] bg-[var(--al-bg)]">
          {existing ? (
            <button
              type="button"
              data-testid="cfg-disconnect"
              onClick={disconnect}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium text-[#DC2626] hover:bg-[#FEE2E2] disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t("cfg.disconnect")}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="cfg-cancel"
              onClick={onClose}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px]"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              data-testid="cfg-save"
              onClick={submit}
              disabled={!canSubmit || saving}
              className="al-btn-primary inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[12.5px] font-semibold disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {existing ? t("cfg.update") : t("cfg.connect")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
